import React, { useEffect, useState } from 'react';
import SplashScreenComponent from '../screens/SplashScreen';
import {
  hasNotificationLaunchIntent,
  subscribeToNotificationLaunchIntent,
} from '../helpers/notificationLaunchIntent';

/**
 * Hide the native launch screen, guarded.
 *
 * `expo-splash-screen` is required lazily (not imported at module scope) because
 * this wrapper is the outermost surface of the app — it renders before
 * AppProviders and therefore before the app's ErrorBoundary — so a missing /
 * stale native module must degrade to a no-op instead of throwing during boot.
 *
 * @returns {void}
 */
const hideNativeSplash = () => {
  try {
    // eslint-disable-next-line global-require
    require('expo-splash-screen').hideAsync?.().catch(() => {});
  } catch (_) {
    // expo-splash-screen unavailable — nothing to hide
  }
};

/**
 * Error boundary that guarantees a splash-render failure can never strand the
 * app on a red box / blank golden screen.
 *
 * The splash (Lottie + expo-splash-screen) mounts OUTSIDE the app's main
 * ErrorBoundary (that one lives inside AppProviders, which the splash renders
 * before). So a native throw here — e.g. a Lottie native method being undefined
 * because the installed native binary is older than the JS bundle
 * ("undefined is not a function") — would otherwise be uncaught AND leave the
 * user forever on the splash, never reaching Login. Catching it and calling
 * `onFail` skips straight to the app instead.
 */
class SplashErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error) {
        console.warn(
            '[SplashScreenWrapper] splash render failed, skipping to app:',
            error?.message || error,
        );
        // The native OS splash is normally hidden by SplashScreen's mount effect;
        // if that never ran because the render threw, hide it here so we don't
        // fall back onto the native launch storyboard.
        hideNativeSplash();
        this.props.onFail?.();
    }

    render() {
        if (this.state.failed) {
            return null;
        }

        return this.props.children;
    }
}

/**
 * SplashScreenWrapper component that handles the splash screen logic
 * This component is memoized to prevent unnecessary re-renders
 */
const SplashScreenWrapper = ({ children, initialVisible = true, route }) => {
  // A launch driven by a notification tap SKIPS the branding splash entirely:
  // the user asked for one specific screen (a mail thread, an order), not for
  // the logo. The Lottie splash costs 1.5-5 s (SPLASH_TIMEOUT_MS + logo fade +
  // APP_RELEASE_DELAY_MS, see screens/SplashScreen.js) and — because the
  // NavigationContainer only mounts once it is done — that whole window is dead
  // time during which the queued deep-link cannot be delivered at all.
  //
  // Evaluated lazily so an intent recorded before React mounted is honoured on
  // the very FIRST render (no splash frame at all): helpers/mailNotifications
  // installs its tap responder and runs its cold-start recovery on the first
  // tick of the bundle, long before App.jsx releases `appIsReady`.
  const [isSplashVisible, setIsSplashVisible] = useState(
    () => initialVisible && !hasNotificationLaunchIntent(),
  );

  useEffect(() => {
    if (route?.params?.successCloseDay) {
      setIsSplashVisible(true);
    }
  }, [route]);

  // The cold-start tap is recovered through an ASYNC native call
  // (Notifications.getLastNotificationResponseAsync), so on a fast boot the
  // intent regularly lands a few hundred ms AFTER first render — while the
  // Lottie is still playing. Cut the animation short the moment it arrives
  // instead of running out the clock. Fires synchronously on subscribe when an
  // intent already exists, which also covers a re-mount (`restartApp`).
  useEffect(() => subscribeToNotificationLaunchIntent(() => {
    setIsSplashVisible(false);
  }), []);

  // Whenever the splash is NOT on screen, SplashScreenComponent's mount effect —
  // the one that normally calls hideAsync() — never runs, so hide the native
  // launch screen here. Without this a skipped splash could fall back onto the
  // static launch storyboard instead of the app.
  useEffect(() => {
    if (!isSplashVisible) {
      hideNativeSplash();
    }
  }, [isSplashVisible]);

  const handleAnimationFinish = () => {
    setIsSplashVisible(false);
  };

  if (isSplashVisible) {
    return (
      <SplashErrorBoundary onFail={handleAnimationFinish}>
        <SplashScreenComponent onAnimationFinish={handleAnimationFinish} />
      </SplashErrorBoundary>
    );
  }

  return children;
};

export default React.memo(SplashScreenWrapper);
